import { z } from "zod";
import { CompositionAspectRatioDto } from "./aspect-ratio.ts";
import { FontStyleDto } from "./font.ts";

export const CaptionDisplayMode = z.enum([
  "STATIC",
  "HIGHLIGHTED",
  "SINGLE_WORD",
]);

export const CaptionAnimationDto = z.object({
  type: z.enum(["FADE_IN", "POP_IN"]),
});

export const CaptionTextTransformDto = z.enum([
  "capitalize",
  "uppercase",
  "lowercase",
]);

export const CaptionShadowDto = z.object({
  color: z.string(),
  opacity: z.number(),
  x: z.number(),
  y: z.number(),
  blur: z.number(),
});

export const CaptionPaddingDto = z.object({
  top: z.number(),
  right: z.number(),
  bottom: z.number(),
  left: z.number(),
});

export const CaptionTextStyleDto = z.object({
  color: z.string().optional(),
  fontSize: z.number().optional(),
  fontWeight: z.number().optional(),
  fontExternalId: z.string(),
  fontStyle: FontStyleDto.optional(),
  textTransform: CaptionTextTransformDto.optional(),
  textShadow: CaptionShadowDto.optional(),
  letterSpacing: z.string().optional(),
  opacity: z.number().optional(),
});

export const CaptionActiveTextStyleDto = z.object({
  color: z.string().optional(),
  opacity: z.number().optional(),
});

export const CaptionBackgroundStyleDto = z.object({
  opacity: z.number().optional(),
  color: z.string().optional(),
  borderRadius: z.number().optional(),
  padding: CaptionPaddingDto.optional(),
  boxShadow: CaptionShadowDto.optional(),
});

const CaptionPositionValueDto = z.object({
  y: z.number().min(0).max(100),
});

export const CaptionPositionDto = z.partialRecord(
  CompositionAspectRatioDto,
  CaptionPositionValueDto.optional(),
);

export const CaptionStyleDto = z.object({
  opacity: z.number().optional(),
  animation: CaptionAnimationDto.optional(),
  position: CaptionPositionDto.optional(),
  text: CaptionTextStyleDto,
  activeText: CaptionActiveTextStyleDto.optional(),
  background: CaptionBackgroundStyleDto.optional(),
});

export const CaptionConfigDto = z.object({
  displayMode: CaptionDisplayMode,
  style: CaptionStyleDto,
});

export const CaptionDto = z.object({
  externalId: z.string(),
  name: z.string(),
  config: CaptionConfigDto,
});

export const CustomCaptionDto = CaptionDto.extend({
  teamExternalId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});

export const CreateCustomCaptionDto = z.object({
  name: z.string().min(1).max(20),
  config: CaptionConfigDto,
});

export const CreateCustomCaptionResponseDto = CustomCaptionDto;

export const UpdateCustomCaptionDto = z.object({
  name: z.string().min(1).max(20),
  config: CaptionConfigDto,
});

export const UpdateCustomCaptionResponseDto = CustomCaptionDto;

export const GetCustomCaptionsResponseDto = z.object({
  captions: z.array(CustomCaptionDto),
});

export const DeleteCustomCaptionResponseDto = z.object({
  success: z.boolean(),
});

export const SeedCaptionPresetsResponseDto = z.object({
  created: z.number(),
  skipped: z.number(),
});

export type CaptionDisplayMode = z.infer<typeof CaptionDisplayMode>;
export type CaptionTextTransformDto = z.infer<typeof CaptionTextTransformDto>;
export type CaptionShadowDto = z.infer<typeof CaptionShadowDto>;
export type CaptionTextStyleDto = z.infer<typeof CaptionTextStyleDto>;
export type CaptionActiveTextStyleDto = z.infer<
  typeof CaptionActiveTextStyleDto
>;
export type CaptionPositionDto = z.infer<typeof CaptionPositionDto>;
export type CaptionPaddingDto = z.infer<typeof CaptionPaddingDto>;
export type CaptionBackgroundStyleDto = z.infer<
  typeof CaptionBackgroundStyleDto
>;
export type CaptionAnimationDto = z.infer<typeof CaptionAnimationDto>;
export type CaptionStyleDto = z.infer<typeof CaptionStyleDto>;
export type CaptionConfigDto = z.infer<typeof CaptionConfigDto>;
export type CaptionDto = z.infer<typeof CaptionDto>;
export type CustomCaptionDto = z.infer<typeof CustomCaptionDto>;
export type CreateCustomCaptionDto = z.infer<typeof CreateCustomCaptionDto>;
export type CreateCustomCaptionResponseDto = z.infer<
  typeof CreateCustomCaptionResponseDto
>;
export type UpdateCustomCaptionDto = z.infer<typeof UpdateCustomCaptionDto>;
export type UpdateCustomCaptionResponseDto = z.infer<
  typeof UpdateCustomCaptionResponseDto
>;
export type GetCustomCaptionsResponseDto = z.infer<
  typeof GetCustomCaptionsResponseDto
>;
export type DeleteCustomCaptionResponseDto = z.infer<
  typeof DeleteCustomCaptionResponseDto
>;
export type SeedCaptionPresetsResponseDto = z.infer<
  typeof SeedCaptionPresetsResponseDto
>;
